import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const egnyte = defineConnector({
  identifier: "egnyte",
  name: "Egnyte",
  tagLine: "Enterprise file sharing and governance",
  logo,
  protocol: "http",
  tags: ["data"],
  defaultUrlPattern: () => "https://[^/]*\\.egnyte\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Egnyte instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Egnyte instance.",
        },
        {
          kind: "input",
          label: "Egnyte Domain",
          placeholder: "domain",
          required: true,
          target: { kind: "configuration", field: "subdomain" },
          helperText:
            "The subdomain of your Egnyte account (e.g. https://domain.egnyte.com)",
        },
      ],
    },
    {
      title: "Create an Egnyte API key",
      items: [
        {
          kind: "markdown",
          content:
            "Go to [developers.egnyte.com](https://developers.egnyte.com/member/register) and register a developer account if you haven’t already. Then create a new application under **My Apps**, choosing **Internal Application** if it’s for your own Egnyte domain. New keys require approval from Egnyte (typically within one business day) and default to 2 requests per second and 1,000 calls per day.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/egnyte/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Define scopes for this connection",
      items: [
        {
          kind: "markdown",
          content:
            "Add [scopes](https://developers.egnyte.com/api-docs/read/public-api-authentication) as a space-separated list below. `Egnyte.filesystem` is enough for file and folder operations.",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "Egnyte.filesystem Egnyte.link",
          required: true,
          defaultValue: "Egnyte.filesystem",
          helperText: "Space-separated list of scopes.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Egnyte account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Egnyte account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "oauthConnection", field: "scopes" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: (configuration) =>
      `https://${configuration.subdomain}.egnyte.com/puboauth/token`,
    tokenEndpoint: (configuration) =>
      `https://${configuration.subdomain}.egnyte.com/puboauth/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => ["Egnyte.filesystem"],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) =>
      `https://${configuration.subdomain}.egnyte.com/pubapi/v1/userinfo`,
  },
  publicEnvVars: ({ subdomain }) => ({
    EGNYTE_URL: subdomain ? `https://${subdomain}.egnyte.com` : "",
  }),
});
